// Cloud persistence API client for Survey Designer → sam-dbam
// Survey Designer-only: surveys, versions, health
import { getServiceUrl } from '../sysconfig';

// Normalize descriptor to kernel rules (preserve graph semantics):
// - Preserve `START` sentinel as a valid source; never rewrite or drop it
// - Preserve `END` sentinel as a valid target; never map it to a completion id
// - Normalize any `start_*` variants to the literal `START`
// - Preserve conditions verbatim and priorities (non-numeric treated as 0)
function normalizeDescriptorForKernel(descriptor) {
  if (!descriptor || typeof descriptor !== 'object') return descriptor;
  const d = { ...descriptor };
  const qs = Array.isArray(d.questions) ? d.questions : [];
  const flow = Array.isArray(d.flow) ? d.flow : [];
  if (qs.length === 0 || flow.length === 0) return d;
  const validIds = new Set(qs.map(q => String(q.question_id || '').trim()).filter(Boolean));

  let edges = [];
  for (const e of flow) {
    let from = String(e.from || '').trim();
    let to = String(e.to || '').trim();
    const isStartVariant = (s) => /^start(?:$|[_-])/i.test(String(s || ''));
    if (isStartVariant(from)) from = 'START';
    if (isStartVariant(to)) to = 'START';
    const condition = (e.condition != null) ? String(e.condition) : '';
    const pNum = Number(e.priority);
    const priority = Number.isFinite(pNum) ? pNum : 0;
    edges.push({ from, to, condition, priority });
  }

  edges = edges.filter(e => {
    const okFrom = (e.from === 'START') || validIds.has(e.from);
    const okTo = (e.to === 'END') || validIds.has(e.to);
    return okFrom && okTo;
  });

  d.flow = edges;
  return d;
}

/**
 * Unwrap sam-dbam response format.
 */
function unwrapSamDbamResponse(resp, expectArray = false) {
  if (!resp || typeof resp !== 'object') return resp;

  // If response has data property, unwrap it
  if (resp.data !== undefined) {
    const inner = resp.data;
    // Nested structure: {data: {data: [...], pagination: {...}}, meta: {...}}
    if (inner && typeof inner === 'object' && Array.isArray(inner.data)) {
      return inner.data;
    }
    // Direct structure: {data: [...], meta: {...}} or {data: {...}, meta: {...}}
    if (expectArray && Array.isArray(inner)) {
      return inner;
    }
    return inner;
  }
  return resp;
}

const getDbamApiBase = async () => {
  return await getServiceUrl('sam_dbam');
};

// ---- Session-death notification --------------------------------------
// The apps authenticate two ways at once: survey calls carry the X-Org-ID /
// X-User-ID headers cached at page load, while cookie-authenticated routes
// (media-refs, package-refs, /me) need a live Hanko session. A session that
// expires in an open tab therefore leaves the first kind working and the
// second kind 401ing, which surfaced as inexplicable save failures instead of
// "you are signed out". Any 401 now tells AuthContext, which drops the user
// and lets the gate render the sign-in form.
let __onUnauthorized = null;

/** Registered once by AuthContext. */
export function setUnauthorizedHandler(fn) {
  __onUnauthorized = typeof fn === 'function' ? fn : null;
}

/** Call with any dbam response; reports a 401 exactly once per response. */
export function noteAuthFailure(resp) {
  if (resp && resp.status === 401 && __onUnauthorized) {
    try { __onUnauthorized(); } catch (_) { /* never let this break a caller */ }
  }
}

// ---- Org context for account-scoped requests ----
// Mirrors console/src/utils/cloudApi.js: dbam scopes surveys by the
// X-Org-ID / X-User-ID headers (see sam-dbam AuthStub middleware, BUG-115).
// Without them the Designer's list/open/save calls arrived context-less and
// dbam could not scope them to the caller's account.
let __orgContext = { orgId: null, userId: null, resolved: false };

/**
 * Set the org context for all subsequent API calls.
 * Called by AuthContext once /dbam/v1/me resolves (and with nulls on logout).
 */
export function setOrgContext(orgId, userId) {
  __orgContext = { orgId: orgId || null, userId: userId || null, resolved: true };
}

/**
 * Resolve the org context lazily. The deep-link bootstrap in index.js runs
 * before AuthContext mounts, so the first API call may need to fetch
 * /dbam/v1/me itself (the Hanko cookie rides along on the credentialed
 * fetch, same as AuthContext.checkAuth).
 */
async function ensureOrgContext() {
  if (__orgContext.resolved) return __orgContext;
  try {
    const base = (await getDbamApiBase()).replace(/\/+$/, '');
    const resp = await fetch(`${base}/dbam/v1/me`, { credentials: 'include' });
    if (resp.ok) {
      const json = await resp.json();
      const profile = json?.data || json;
      __orgContext = {
        orgId: profile?.org_id || null,
        userId: profile?.user_id || null,
        resolved: true,
      };
      return __orgContext;
    }
  } catch (_) { /* fall through — treat as unauthenticated */ }
  __orgContext = { ...__orgContext, resolved: true };
  return __orgContext;
}

/**
 * Build org-context headers for dbam fetches.
 */
async function orgHeaders(extra = {}) {
  const ctx = await ensureOrgContext();
  const h = { ...extra };
  if (ctx.orgId) h['X-Org-ID'] = ctx.orgId;
  if (ctx.userId) h['X-User-ID'] = ctx.userId;
  return h;
}

/**
 * Generic HTTP request with timeout and response-type handling.
 */
async function httpRequest(url, options = {}, { timeoutMs = 10000, responseType = 'json' } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(url, { ...options, signal: controller.signal });
    if (!resp.ok) {
      noteAuthFailure(resp);
      let errText = '';
      try { errText = await resp.text(); } catch (_) { }
      let code = null;
      try {
        const parsed = JSON.parse(errText);
        if (parsed && parsed.error && (parsed.error.code || parsed.code)) {
          code = parsed.error.code || parsed.code;
        }
      } catch (_) { }
      const message = errText || resp.statusText || 'Request failed';
      const error = new Error(message);
      error.status = resp.status;
      if (code) error.code = code;
      throw error;
    }
    if (responseType === 'json') {
      return await resp.json();
    }
    return await resp.text();
  } catch (err) {
    if (err && (err.name === 'AbortError' || /aborted/i.test(String(err.message)))) {
      const timeoutError = new Error(`Request timed out after ${timeoutMs} ms`);
      timeoutError.code = 'ETIMEOUT';
      throw timeoutError;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

let __validatedKernelBaseCache = { base: null, validatedAt: 0 };

/**
 * Resolve and validate the API base by hitting a health endpoint.
 */
export async function getValidatedKernelApiBase({ timeoutMs = 2500 } = {}) {
  const now = Date.now();
  if (__validatedKernelBaseCache.base && now - __validatedKernelBaseCache.validatedAt < 60000) {
    return __validatedKernelBaseCache.base;
  }
  const base = (await getDbamApiBase()).replace(/\/$/, '');
  const tryUrls = [base + '/dbam/v1/health'];
  let ok = false;
  for (const u of tryUrls) {
    try {
      await httpRequest(u, { method: 'GET' }, { timeoutMs, responseType: 'json' });
      ok = true;
      break;
    } catch (_) { }
  }
  if (!ok) {
    throw new Error('Health check failed; cannot validate API base');
  }
  __validatedKernelBaseCache = { base, validatedAt: now };
  return base;
}

/**
 * Fetch health status.
 */
export async function fetchKernelHealth({ timeoutMs = 2500 } = {}) {
  const base = (await getDbamApiBase()).replace(/\/$/, '');
  const url = base + '/dbam/v1/health';
  try {
    const body = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
    return { ok: true, status: 200, body };
  } catch (e) {
    return { ok: false, status: e.status || 0, error: e.message };
  }
}

// ---- Survey CRUD ----

// Caller MUST supply userId and/or orgId from the authenticated user
// (see useAuth() in src/context/AuthContext.js). dbam's CreateSurvey
// validates "exactly one of user_id or org_id", so passing neither will
// 400 and passing both will also 400. Today we pass user_id (single-author
// surveys); when team/org surveys land, prefer org_id instead.
export async function createSurveyInCloud({ name, descriptor, initialStatus, designerGraph, designerPayload, userId, orgId, kind, settings, tags }) {
  if (!userId && !orgId) {
    throw new Error('createSurveyInCloud: userId or orgId is required (no logged-in user?)');
  }
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys`;
  const cleaned = normalizeDescriptorForKernel(descriptor);

  const body = {
    name,
    descriptor: cleaned,
    initial_status: initialStatus,
    designer_graph: designerGraph,
    designer_payload: designerPayload,
    target_channels: cleaned?.survey_descriptor?.target_channels || ['browser'],
  };
  // Survey Packages (WP-5): a package is created as an ordinary survey row
  // discriminated by asset_kind. Omitting kind keeps the server default 'survey'.
  if (kind === 'package') body.asset_kind = 'package';
  if (settings && typeof settings === 'object') body.settings = settings;
  if (tags) body.tags = tags;
  if (orgId) body.org_id = orgId;
  else body.user_id = userId;

  let resp;
  try {
    resp = await fetch(url, {
      method: 'POST',
      // credentials: include sends the Hanko cookie along to dbam so the
      // request is authenticated under the same session that the /me call
      // resolved. Required because dbam's CORS allowlist is credentialed.
      credentials: 'include',
      headers: await orgHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
  } catch (fetchError) {
    throw new Error(`Failed to connect to server: ${fetchError.message}. Check if the server is running.`);
  }

  if (!resp.ok) {

    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    if (resp.status >= 500) {
      throw new Error(`Server error (${resp.status}): ${text || 'Internal server error'}`);
    }
    throw new Error(`Survey create failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function listSurveysFromCloud({ limit = 50, offset = 0, search = '', kind = '' } = {}) {
  const apiBase = await getDbamApiBase();
  // kind='package' lists packages; omitting it keeps the server default (surveys
  // only), so console and the ordinary Open list never see packages (WP-3/WP-5).
  const kindParam = kind === 'package' ? `&kind=package` : '';
  const url = `${apiBase}/dbam/v1/surveys?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}&search=${encodeURIComponent(search)}${kindParam}`;
  const resp = await fetch(url, { headers: await orgHeaders() });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`List surveys failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json();
  return { items: json.data || [] };
}

export async function getSurveySummaryFromCloud(surveyId) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}`;
  const resp = await fetch(url, { headers: await orgHeaders() });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Fetch survey failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json();
  return json.data;
}

export async function updateSurveyInCloud({ surveyId, updates }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}`;
  const resp = await fetch(url, {
    method: 'PATCH',
    headers: await orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(updates || {})
  });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    if (resp.status === 404) throw new Error('Survey not found');
    throw new Error(`Survey update failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function deleteSurveyInCloud({ surveyId }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: await orgHeaders() });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Delete survey failed (${resp.status}): ${text || resp.statusText}`);
  }
  // sam-dbam returns 204 No Content on delete
  return { ok: true };
}

// Server-side clone via dbam's POST /surveys/{id}/clone endpoint.
// Backend assigns a new survey ID and names the copy "Copy of {original name}".
// Returns the new survey envelope (data + meta).
export async function copySurveyInCloud({ surveyId }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/clone`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: await orgHeaders({ 'Content-Type': 'application/json' })
  });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Copy survey failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json();
  return json.data || json;
}

// ---- Survey Versions ----

// List all versions of a survey/package (newest first), for version history.
export async function listSurveyVersionsFromCloud(surveyId) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/versions`;
  const resp = await fetch(url, { headers: await orgHeaders() });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`List versions failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json();
  return json.data || [];
}

export async function createSurveyVersionInCloud({ surveyId, descriptor, status, publish, notes, designerGraph, designerPayload }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/versions`;
  const cleaned = normalizeDescriptorForKernel(descriptor);
  const resp = await fetch(url, {
    method: 'POST',
    headers: await orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ descriptor: cleaned, status, publish, notes, designer_graph: designerGraph, designer_payload: designerPayload })
  });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Version create failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function getSurveyVersionFromCloud(surveyId, versionNumber) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}`;
  const resp = await fetch(url, { headers: await orgHeaders() });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Fetch version failed (${resp.status}): ${text || resp.statusText}`);
  }
  const data = await resp.json();
  const unwrapped = unwrapSamDbamResponse(data);
  const etag = unwrapped?.etag || resp.headers.get('ETag') || null;
  return { data: unwrapped, etag };
}

export async function updateSurveyVersionInCloud({ surveyId, versionNumber, descriptor, status, notes, designerPayload, etag }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}`;
  const headers = await orgHeaders({ 'Content-Type': 'application/json' });
  if (etag) headers['If-Match'] = `"${etag}"`;
  const cleaned = descriptor ? normalizeDescriptorForKernel(descriptor) : undefined;

  let resp;
  try {
    resp = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ descriptor: cleaned, status, notes, designer_payload: designerPayload })
    });
  } catch (fetchError) {
    throw new Error(`Failed to connect to server: ${fetchError.message}.`);
  }

  if (!resp.ok) {

    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    // sam-dbam errors arrive as {"error":{"code","message"},...} — extract the
    // structured code/message so authors never see a raw JSON dump.
    let errCode = '';
    let errDetail = '';
    try {
      const parsed = JSON.parse(text);
      errCode = parsed?.error?.code || '';
      errDetail = parsed?.error?.message || '';
    } catch (_) { /* non-JSON body — fall through to generic text */ }

    if (resp.status === 412) {
      throw new Error('Version update failed: Survey was modified by another user. Please refresh and try again.');
    }
    if (resp.status === 404) {
      throw new Error('Survey or version not found. It may have been deleted.');
    }
    if (errCode === 'PINNED_VERSION_IMMUTABLE') {
      throw new Error(
        'This version is deployed, so its wording is locked in place — everyone taking the survey must see the same questions. ' +
        'To make your changes, save them as a new version (the arrow next to Save Overwrite), ' +
        'or undeploy the survey first from the Console’s Deployed panel.'
      );
    }
    if (resp.status >= 500) {
      throw new Error(`Server error (${resp.status}): ${errDetail || 'Internal server error'}`);
    }
    throw new Error(`Save failed: ${errDetail || text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function deleteSurveyVersionInCloud({ surveyId, versionNumber }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: await orgHeaders() });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Delete version failed (${resp.status}): ${text || resp.statusText}`);
  }
  // sam-dbam returns 204 No Content on delete (route not yet mounted)
  return { ok: true };
}

/**
 * Uploads a local media file to the sam-dbam media upload endpoint.
 */
export async function uploadMediaAsset(file) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/media-assets/upload`;
  const formData = new FormData();
  formData.append('file', file);

  let resp;
  try {
    resp = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
  } catch (fetchError) {
    throw new Error(`Failed to connect to server: ${fetchError.message}. Check if the server is running.`);
  }

  if (!resp.ok) {

    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Media upload failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json();
  return unwrapSamDbamResponse(json);
}

/**
 * Ref-sync endpoints authenticate on the Hanko session cookie, unlike the
 * survey save endpoints, which authenticate on the X-Org-ID / X-User-ID
 * headers cached at page load. A session that expires while the tab stays open
 * therefore leaves saving working and ref-syncing 401ing — so the raw
 * NO_SESSION body is translated into the one thing that actually helps.
 */
function refSyncError(kind, resp, text) {
  if (resp.status === 401) {
    return new Error(`your sign-in has expired \u2014 open the Designer again to sign back in, then re-save to sync ${kind}`);
  }
  return new Error(`Failed to sync ${kind} (${resp.status}): ${text || resp.statusText}`);
}

/**
 * Synchronizes media references for a specific survey version.
 */
export async function syncMediaReferences({ surveyId, versionNumber, refs }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}/media-refs`;
  
  let resp;
  try {
    resp = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refs),
    });
  } catch (fetchError) {
    throw new Error(`Failed to connect to server: ${fetchError.message}.`);
  }

  if (!resp.ok) {

    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw refSyncError('media links', resp, text);
  }
  return unwrapSamDbamResponse(await resp.json());
}

/**
 * Synchronizes package references for a specific survey version (Survey
 * Packages WP-4). Mirrors syncMediaReferences: PUT the full ref set for the
 * version; the server replaces the prior set transactionally.
 *
 * refs: [{ package_id, package_version, node_id }]
 */
export async function syncPackageReferences({ surveyId, versionNumber, refs }) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}/package-refs`;

  let resp;
  try {
    resp = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refs),
    });
  } catch (fetchError) {
    throw new Error(`Failed to connect to server: ${fetchError.message}.`);
  }

  if (!resp.ok) {

    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw refSyncError('package links', resp, text);
  }
  return unwrapSamDbamResponse(await resp.json());
}

/**
 * Reverse lookup: surveys whose current version embeds the given package
 * (Survey Packages WP-4). Returns [{ survey_id, name, version }].
 */
export async function listPackageEmbedders(packageId) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/packages/${encodeURIComponent(packageId)}/refs?current=true`;
  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to list package embedders (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json()) || [];
}

// ---- User preferences (per-user, GCS-backed via sam-dbam) ----
//
// One JSON object per user (see docs/designer_layout_and_appearance_spec.md).
// The designer keeps its canvas appearance settings under prefs.designer.canvas.

export async function fetchUserPrefsFromCloud() {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/user-prefs`;
  const resp = await fetch(url, {
    credentials: 'include',
    headers: await orgHeaders(),
  });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Fetch user prefs failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json().catch(() => ({}));
  return unwrapSamDbamResponse(json) || {};
}

export async function saveUserPrefsToCloud(prefs) {
  const apiBase = await getDbamApiBase();
  const url = `${apiBase}/dbam/v1/user-prefs`;
  const resp = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: await orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(prefs || {}),
  });
  if (!resp.ok) {
    noteAuthFailure(resp);
    const text = await resp.text().catch(() => '');
    throw new Error(`Save user prefs failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json().catch(() => ({})));
}

// ---- Plan limits ----------------------------------------------------------
//
// Design: docs/tier-enforcement-method.md
//
// The survey assistant spends LLM tokens, and tokens cost money. A free account
// that has used up its surveys does not get to keep spending them, so the
// designer asks sam-dbam before opening the assistant panel.
//
// A failed check returns enabled: true. Refusing the assistant because a status
// call did not answer would punish the wrong person.
export async function fetchAssistantsEnabled() {
  try {
    const apiBase = await getDbamApiBase();
    const resp = await fetch(`${apiBase}/dbam/v1/limits/assistants`, {
      credentials: 'include',
      headers: await orgHeaders(),
    });
    if (!resp.ok) return { enabled: true, message: '' };
    const data = unwrapSamDbamResponse(await resp.json().catch(() => ({})));
    return { enabled: data?.enabled !== false, message: data?.message || '' };
  } catch (_) {
    return { enabled: true, message: '' };
  }
}
